import {
  LE_V2_04_CONTRACT_VERSION,
  type BatchSubmissionItemV2,
  type BatchSubmissionV2,
} from './batchContract';
import {
  LE_V2_05_CONTRACT_VERSION,
  type CorrectionSubmissionItemV2,
  type CorrectionSubmissionV2,
} from './correctionContract';
import type { MathWorksheetSetV2 } from '../learningOrchestrator/worksheetTypes';
import type { MathAnswerDraftMapV2 } from '../studyV2/answerCapture';
import { isMathAnswerReady } from '../studyV2/answerCapture';
import type { ProblemTimingSnapshotV2 } from '../studyV2/problemActivity';

export type StudyBatchSubmissionContextV2 = {
  clientBatchSubmissionId: string;
  studySessionId: string;
  assignmentId: string;
  worksheetVersionId: string;
  submittedAt: string;
  telemetry: BatchSubmissionV2['telemetry'];
};

export type CorrectionSubmissionContextV2 = {
  clientCorrectionSubmissionId: string;
  clientBatchSubmissionId: string;
  studySessionId: string;
  assignmentId: string;
  submittedAt: string;
};

function flatProblems(worksheet: MathWorksheetSetV2) {
  return worksheet.pages.flatMap((page) => page.problems.map((problem) => ({ pageId: page.pageId, problem })));
}

export function buildStudyBatchSubmissionV2(input: {
  worksheet: MathWorksheetSetV2;
  answersByProblemId: MathAnswerDraftMapV2;
  timingByProblemId: Record<string, ProblemTimingSnapshotV2 | undefined>;
  context: StudyBatchSubmissionContextV2;
}): BatchSubmissionV2 {
  const items: BatchSubmissionItemV2[] = flatProblems(input.worksheet).map(({ problem }) => {
    const binding = problem.submissionBinding;
    if (!binding) throw new Error(`Problem ${problem.problemId} has no authoritative submission binding.`);
    const answer = input.answersByProblemId[problem.problemId];
    if (!isMathAnswerReady(answer)) throw new Error(`Problem ${problem.problemId} has no valid structured final answer.`);
    const timing = input.timingByProblemId[problem.problemId];
    if (!timing) throw new Error(`Problem ${problem.problemId} has no timing snapshot.`);
    return {
      sequence: binding.sequence,
      assignmentItemId: binding.assignmentItemId,
      questionInstanceId: binding.questionInstanceId,
      response: answer.response,
      startedAt: timing.startedAt,
      completedAt: timing.completedAt,
      elapsedDurationMs: timing.elapsedDurationMs,
      activeDurationMs: timing.activeDurationMs,
      hintCount: 0,
      ink: null,
    };
  });

  return {
    schemaVersion: 2,
    contractVersion: LE_V2_04_CONTRACT_VERSION,
    clientBatchSubmissionId: input.context.clientBatchSubmissionId,
    studySessionId: input.context.studySessionId,
    assignmentId: input.context.assignmentId,
    worksheetVersionId: input.context.worksheetVersionId,
    submittedAt: input.context.submittedAt,
    telemetry: input.context.telemetry,
    items: items.sort((a, b) => a.sequence - b.sequence),
  };
}

export function buildStudyCorrectionSubmissionV2(input: {
  worksheet: MathWorksheetSetV2;
  remainingQuestionInstanceIds: readonly string[];
  correctionAnswersByQuestionInstanceId: MathAnswerDraftMapV2;
  timingByQuestionInstanceId: Record<string, ProblemTimingSnapshotV2 | undefined>;
  context: CorrectionSubmissionContextV2;
}): CorrectionSubmissionV2 {
  const targetSet = new Set(input.remainingQuestionInstanceIds);
  const boundByQuestion = new Map(
    flatProblems(input.worksheet)
      .map(({ problem }) => problem.submissionBinding ? [problem.submissionBinding.questionInstanceId, problem] as const : null)
      .filter((row): row is readonly [string, (ReturnType<typeof flatProblems>)[number]['problem']] => Boolean(row)),
  );

  const items: CorrectionSubmissionItemV2[] = input.remainingQuestionInstanceIds.map((questionInstanceId) => {
    if (!targetSet.has(questionInstanceId)) throw new Error(`Question ${questionInstanceId} is not an active correction target.`);
    const problem = boundByQuestion.get(questionInstanceId);
    if (!problem) throw new Error(`Correction target ${questionInstanceId} is not present in the worksheet.`);
    const answer = input.correctionAnswersByQuestionInstanceId[questionInstanceId];
    if (!isMathAnswerReady(answer)) throw new Error(`Correction target ${questionInstanceId} has no valid corrected answer.`);
    const timing = input.timingByQuestionInstanceId[questionInstanceId];
    if (!timing) throw new Error(`Correction target ${questionInstanceId} has no timing snapshot.`);
    return {
      questionInstanceId,
      correctedResponse: answer.response,
      startedAt: timing.startedAt,
      completedAt: timing.completedAt,
      elapsedDurationMs: timing.elapsedDurationMs,
      activeDurationMs: timing.activeDurationMs,
      hintCount: 0,
      ink: null,
    };
  });

  return {
    schemaVersion: 2,
    contractVersion: LE_V2_05_CONTRACT_VERSION,
    clientCorrectionSubmissionId: input.context.clientCorrectionSubmissionId,
    clientBatchSubmissionId: input.context.clientBatchSubmissionId,
    studySessionId: input.context.studySessionId,
    assignmentId: input.context.assignmentId,
    submittedAt: input.context.submittedAt,
    items,
  };
}
